import { timestripRegionName } from 'owa-locstrings/lib/strings/timestripregionname.locstring.json';
import { calendarErrorItemNoLongerExist } from './peekRouteHandler.locstring.json';
import loc, { format as stringFormat } from 'owa-localize';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import initializeState from '../initializeState';
import { pendingActions, selectCalendar } from 'owa-calendar-surface-select-calendar';
import { getCalendarEventFromSurface, surfaceStore } from 'owa-calendar-surface-surface-store';
import { fetchFullCalendarEvent } from 'owa-calendar-surface-full-event-locked-store';
import viewRouteHandler from './viewRouteHandler';
import {
    CalendarViewRouteParameters,
    PeekRouteParameters,
    USER_CALENDAR_VIEW_TYPE_ROUTE,
} from './calendarRoutes';
import { confirm } from 'owa-confirm-dialog';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { endAsyncDatapoint } from 'owa-calendar-data-utils/lib/endAsyncDatapoint';
import { getCalendarEntryByCalendarId, getCalendarIdByFolderId } from 'owa-calendar-cache';
import { getFocusableSurfaceComponent, selectItem } from 'owa-calendar-surface-view';
import { getOverflowIndicatorId } from 'owa-calendar-surface-dategrid';
import { findSelectedCalendar } from 'owa-calendar-surface-calendars-store';
import { getUserMailboxInfo } from 'owa-client-ids';
import { openItemPeek } from 'owa-calendar-peek';
import { getYear, getMonth, getDate } from 'owa-datetime';
import { lazyLoadOptions } from 'owa-outlook-service-options';

/**
 * The route handler for event peek deeplink
 */
export default async function peekRouteHandler(parameters: PeekRouteParameters) {
    let dataPoint = new PerformanceDatapoint('showPeekHandler');
    let item: CalendarEvent;
    let error: any;

    // TODO-zorauf VSO #28930: See if this needs to be handled for groups as well?
    const { fullEventPromise } = fetchFullCalendarEvent(
        {
            Id: parameters.itemId,
            mailboxInfo: getUserMailboxInfo(),
        },
        undefined,
        getCalendarEventFromSurface,
        undefined /** isfetchFullSeriesMasterCalendarEventFromEventInstance */,
        { fetchSource: 'PeekRouteHandler' }
    );

    [error, item] = await to(fullEventPromise);

    if (!item?.ParentFolderId) {
        // If there is some kind of error retrieving the item (due to missing or incorrect itemid),
        // an error message is shown that the item wasn't found.
        await confirm('', loc(calendarErrorItemNoLongerExist), false, {
            hideCancelButton: true,
        });

        endDataPoint(dataPoint, 'calendarItemNotFound');
        return;
    }

    if (error) {
        endDataPoint(dataPoint, 'loadCalendarEvent', error);
        return;
    }

    // Wait till all the calendars get loaded and the calendars cache gets initialized
    try {
        initializeState();
    } catch (error) {
        endDataPoint(dataPoint, 'initializeState', error);
        return;
    }

    // Bootstrap intializes getitems call for all the selected calendars to fetch 1 month data asynchronously
    // in which case, the above call doesn't initiate a getitems call again because there is already a
    // pending promise; await on this promise so that the items get loaded onto the surface.
    if (item.ParentFolderId.Id in pendingActions) {
        [error] = await to(pendingActions[item.ParentFolderId.Id]);
        if (error) {
            endDataPoint(dataPoint, 'pendingActions', error);
            return;
        }
    }

    // Display the preferred view with the event date falling in the view date range
    let viewRouteHandlerParams: CalendarViewRouteParameters = {
        view: USER_CALENDAR_VIEW_TYPE_ROUTE, // use this string, so that the preferred view gets displayed
        y: getYear(item.Start),
        m: getMonth(item.Start) + 1,
        d: getDate(item.Start),
        // force fall back to surface view type if user's preferred view is not surface
        // TODO: VSO #93977: Rationalize module routing when canvas view type is explicitly specified for surface-only routes
        forceSurfaceViewType: true,
    };

    // Wait till the calendar has actually rendered and loaded the surface items
    // before setting the focus on the item; else scrolling won't happen
    [error] = await to(viewRouteHandler(viewRouteHandlerParams));
    if (error) {
        endDataPoint(dataPoint, 'viewRouteHandler', error);
        return;
    }
    if (surfaceStore.dateRangeType === DateRangeType.Month) {
        /** if we are in month view wait for the options to load to ensure
         * the mogenda open/ close state has been set before rendering the peek
         */
        await lazyLoadOptions.importAndExecute();
    }
    // If the event related calendar is not selected, select it
    let calendarId = getCalendarIdByFolderId(item.ParentFolderId.Id);
    let isSelectedCal = findSelectedCalendar(calendarId.id);
    if (!isSelectedCal) {
        let calendarToSelect = getCalendarEntryByCalendarId(calendarId.id);
        if (calendarToSelect) {
            [error] = await to(selectCalendar(calendarToSelect));
            if (error) {
                endDataPoint(dataPoint, 'selectCalendar', error);
                return;
            }
        } else {
            return;
        }
    }

    let getQuerySelector = (tag, attribute, value) =>
        stringFormat('{0}[{1}="{2}"]', tag, attribute, value);
    let querySelector;
    let customSelectorId = parameters.itemId;
    let targetHtmlElement = document.getElementById(customSelectorId);
    if (targetHtmlElement) {
        // If the item is present on the surface, set focus on it;
        // this does scrolling if the item is not in the current view.
        getFocusableSurfaceComponent().focusItem(customSelectorId);
        querySelector = getQuerySelector('div', 'id', customSelectorId);
    } else if (surfaceStore.dateRangeType == DateRangeType.Month) {
        // Item behind the event day's overflow indicator;
        // show the peek pointing to this overflow indicator.
        customSelectorId = getOverflowIndicatorId(item.Start);
        targetHtmlElement = document.getElementById(customSelectorId);
        querySelector = getQuerySelector('div', 'id', customSelectorId);
    } else if (surfaceStore.dateRangeType == DateRangeType.WorkWeek) {
        // Event on weekends; show the peek pointing to the surface timestrip.
        querySelector = getQuerySelector('', 'title', loc(timestripRegionName));
        targetHtmlElement = document.querySelector(querySelector);
    }

    // Select the event and open its peek
    if (targetHtmlElement) {
        selectItem(surfaceStore, item, null, querySelector);
        [error] = await to(Promise.resolve(openItemPeek.importAndExecute(item)));
        if (error) {
            endDataPoint(dataPoint, 'openItemPeek', error);
        } else {
            endAsyncDatapoint(dataPoint);
        }
    } else {
        endDataPoint(dataPoint, 'targetHtmlElement', error);
    }
}

/** Wrapper function used to await the promise and parse the error,data for logging */
function to<T>(promise: Promise<T>) {
    return promise.then(data => [null, data]).catch(error => [error]);
}

/** Wrapper function to end the performanceDatapoint */
function endDataPoint(dataPoint: PerformanceDatapoint, customProperty?: string, error?: any) {
    if (customProperty) {
        dataPoint.addCustomProperty('errorAt', customProperty);
    }
    dataPoint.endWithError(DatapointStatus.ClientError, error);
}
